import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, pipe } from 'rxjs';
import { concatMap, map, catchError } from 'rxjs/operators';
import { StuplayConfig } from '../stuplay-config';
import { CourseSubscriptionSession } from "./course-subscription-session";
import { getParams } from "../helpers";
import { StorageService } from 'src/app/utils/services/storage.service';
import { CourseProgressService } from 'src/app/graphql/course-progress.service';

@Injectable({
    providedIn: 'root'
})
export class CourseSubscriptionSessionProvider {

    constructor(
        public http: HttpClient,
        private stuplayConfig: StuplayConfig,
        private storageService: StorageService,
        private courseProgressService: CourseProgressService,
    ) {}

    private statisticsAggregator() {
        return pipe(
            concatMap((data: any) => {
                const sessionId = data?.id;
                if (!sessionId) return of(data);

                return this.courseProgressService.getCourseProgress(sessionId).pipe(
                    map((progress) => {
                        // Build a lookup map: sectionContentId → { doneAt, duration }
                        const unitMap: Record<string, { doneAt: string | null; duration: number }> = {};
                        for (const section of progress.sections) {
                            for (const unit of section.units) {
                                unitMap[unit.sectionContentId] = { doneAt: unit.doneAt, duration: unit.duration };
                            }
                        }

                        // Enrich existing sectionContentSessions with GraphQL data
                        data.sectionContentSessions = (data.sectionContentSessions ?? []).map((scs: any) => {
                            const gql = unitMap[String(scs.sectionContentId)];
                            if (!gql) return scs;
                            return {
                                ...scs,
                                doneAt: gql.doneAt ?? scs.doneAt,
                                duration: gql.duration ?? scs.duration,
                                progress: gql.doneAt ? 100 : 0,
                            };
                        });

                        // Attach top-level progress for course completion badge.
                        // Derive from completed/total to avoid rounding mismatch with REST
                        // (e.g. GraphQL returning 97 while all units are done).
                        if (progress.totalUnits > 0 && progress.completedUnits >= progress.totalUnits) {
                            data.progress = 100;
                        } else if (progress.totalUnits > 0) {
                            data.progress = Math.round((progress.completedUnits / progress.totalUnits) * 100);
                        } else {
                            data.progress = progress.progressPercent;
                        }

                        return data;
                    }),
                    catchError(() => of(data)),
                );
            }),
            map((data) => new CourseSubscriptionSession(data, this.http, this.stuplayConfig)),
        );
    }

    getOrCreateCourseSubscriptionSession(courseId: number, preview?: boolean, directPlay?: boolean): Observable<CourseSubscriptionSession> {
        const params = {
            course_id: courseId,
            direct_play: directPlay ? 1 : 0
        };
        const prev = (preview) ? '?preview=' + preview : '';

        return this.http.post(`${this.stuplayConfig.envVar.API_URL}/course-subscription-sessions${prev}`, params)
            .pipe(this.statisticsAggregator());
    }

    getCourseSubscriptionSession(id: number): Observable<CourseSubscriptionSession> {
        return this.http.get(`${this.stuplayConfig.envVar.API_URL}/course-subscription-sessions/${id}`)
            .pipe(this.statisticsAggregator());
    }

    putCourseSubscriptionSession(id: number, body: any, params: any): Observable<any> {
        return this.http.put(`${this.stuplayConfig.envVar.API_URL}/course-subscription-sessions/${id}`, body, { params });
    }

    resetCourseSubscriptionSession(id: number): Observable<any> {
        return this.http.delete(`${this.stuplayConfig.envVar.API_URL}/course-subscription-sessions/${id}/reset`);
    }

    unsubscribe(id: number, params: any): Observable<any> {
        return this.http.delete(`${this.stuplayConfig.envVar.API_URL}/subscriptions/courses/${id}`, { params: getParams(params) });
    }

    resetContentSectionSession(id: number): Observable<any> {
        return this.http.post(`${this.stuplayConfig.envVar.API_URL}/section-content-sessions/${id}/reset`, {});
    }
}
